/**
 * The project Overview (decision 0051, info model §4 and §3.4): every block derived on read from the project state and
 * the comment queries, shared by the web, the e2e and `read_graph` (one decision list). Pure, no clock: `now` is an
 * input. Items sort by `rank` then `ref` unless said otherwise; a "release" is a top-level item of type `release`.
 * `overviewOf` builds one `TreeIndex` and passes it on, so no selector scans `s.items` per descendant.
 */
import type { ArtifactKind as ArtifactKindSchema } from "@minga/protocol"
import { kpiPeriodLabel, kpiValueText } from "./kpi-period"
import { isOutput, openReviewCards, reviewOutputs } from "./outputs"
import { readiness } from "./readiness"
import { isOpenItem, isOpenProposal, kpiOf, runOf } from "./state"
import {
  indexedDescendants,
  isPlannable,
  NOT_COUNTED_TYPES,
  type Progress,
  progressFromLeaves,
  progressLeaves,
  type TreeIndex,
  treeIndexOf,
} from "./tree"
import type {
  Artifact,
  Comment,
  EntityRef,
  Goal,
  Item,
  ItemId,
  ItemStatus,
  Kpi,
  ProjectState,
  Proposal,
  ProposalBody,
  RunId,
  UserId,
} from "./types"

type ArtifactKind = typeof ArtifactKindSchema.Type

// ---- ordering helpers -----------------------------------------------------------------------------------------

const byRank = (a: Pick<Item, "rank" | "ref">, b: Pick<Item, "rank" | "ref">): number =>
  a.rank < b.rank ? -1 : a.rank > b.rank ? 1 : a.ref < b.ref ? -1 : a.ref > b.ref ? 1 : 0

/** Ascending by a nullable time, null last. */
const byDue = (a: number | null, b: number | null): number =>
  a === b ? 0 : a === null ? 1 : b === null ? -1 : a - b

const isRelease = (t: Item): boolean => t.parentId === null && t.type === "release"

const itemsById = (s: ProjectState): ReadonlyMap<string, Item> => new Map(s.items.map((t) => [t.id, t]))

/** The top-level item above `t` (itself when top-level); stops at a missing parent or a cycle. */
const topOf = (byId: ReadonlyMap<string, Item>, t: Item): Item => {
  let cur = t
  const seen = new Set<string>([t.id])
  while (cur.parentId !== null) {
    const up = byId.get(cur.parentId)
    if (!up || seen.has(up.id)) break
    seen.add(up.id)
    cur = up
  }
  return cur
}

// ---- phase, breakdown, "+n this week" -------------------------------------------------------------------------

/** A milestone's phase, derived from its status (info model §3.4). */
export type Phase = "idea" | "planning" | "building" | "shipped" | "implemented" | "dropped"

const PHASES: Readonly<Record<ItemStatus, Phase>> = {
  triage: "idea",
  backlog: "idea",
  todo: "planning",
  in_progress: "building",
  blocked: "building",
  in_review: "shipped",
  done: "implemented",
  cancelled: "dropped",
}

/** Later → idea, Next → planning, Now (or blocked) → building, in review → shipped, done → implemented, cancelled → dropped. */
export const phaseOf = (status: ItemStatus): Phase => PHASES[status]

/** The counted leaves of a milestone in three lists (they add up to `progressOf(…).total`). */
export interface Breakdown {
  /** status done */
  readonly done: ReadonlyArray<Item>
  /** in progress or in review */
  readonly inProgress: ReadonlyArray<Item>
  /** every other status (todo, backlog, triage, blocked) */
  readonly missing: ReadonlyArray<Item>
}

const breakdownFromLeaves = (leaves: ReadonlyArray<Item>): Breakdown => {
  const done: Item[] = []
  const inProgress: Item[] = []
  const missing: Item[] = []
  for (const l of leaves) {
    if (l.status === "done") done.push(l)
    else if (l.status === "in_progress" || l.status === "in_review") inProgress.push(l)
    else missing.push(l)
  }
  return { done, inProgress, missing }
}

/** Done / In progress / Missing over `progressLeaves` (tree order). */
export const breakdownOf = (s: ProjectState, id: ItemId, ix?: TreeIndex): Breakdown =>
  breakdownFromLeaves(progressLeaves(s, id, ix))

const countSince = (leaves: ReadonlyArray<Item>, since: number): number =>
  leaves.filter((l) => l.createdAt >= since).length

/** The counted leaves created at or after `since` ("+n this week" with `since = now - WEEK_MS`). */
export const addedSince = (s: ProjectState, id: ItemId, since: number, ix?: TreeIndex): number =>
  countSince(progressLeaves(s, id, ix), since)

export const WEEK_MS = 7 * 86_400_000

// ---- update lines ---------------------------------------------------------------------------------------------

/** The four labelled lines of a lead's update (`post_update`). */
export type UpdateLabel = "Built" | "Left" | "Unknown" | "Next"

const UPDATE_LINE_MAX = 200

const LABEL_RE: Readonly<Record<UpdateLabel, RegExp>> = {
  Built: /^\s*(?:[-*]\s*)?(?:\*\*)?Built(?:\*\*)?\s*:\s*(?:\*\*)?\s*(.+)$/i,
  Left: /^\s*(?:[-*]\s*)?(?:\*\*)?Left(?:\*\*)?\s*:\s*(?:\*\*)?\s*(.+)$/i,
  Unknown: /^\s*(?:[-*]\s*)?(?:\*\*)?Unknown(?:\*\*)?\s*:\s*(?:\*\*)?\s*(.+)$/i,
  Next: /^\s*(?:[-*]\s*)?(?:\*\*)?Next(?:\*\*)?\s*:\s*(?:\*\*)?\s*(.+)$/i,
}

/**
 * The text after the first line labelled `label` ("Next: …", "**Next:** …", "- Next: …", any case), trimmed, at most
 * 200 characters; null when there is no such line or it is empty.
 */
export const updateLine = (body: string, label: UpdateLabel): string | null => {
  const re = LABEL_RE[label]
  for (const line of body.split(/\r?\n/)) {
    const m = re.exec(line)
    if (!m) continue
    const text = (m[1] ?? "").trim()
    if (text === "") return null
    return text.length > UPDATE_LINE_MAX ? `${text.slice(0, UPDATE_LINE_MAX - 1).trimEnd()}…` : text
  }
  return null
}

const isNewer = (c: Comment, cur: Comment): boolean =>
  c.createdAt > cur.createdAt || (c.createdAt === cur.createdAt && c.contentSeq > cur.contentSeq)

/** The newest `update` per item or goal (by `createdAt`, then `contentSeq`), keyed `item:<id>` / `goal:<id>`. */
export const newestUpdates = (updates: ReadonlyArray<Comment>): ReadonlyMap<string, Comment> => {
  const newest = new Map<string, Comment>()
  for (const c of updates) {
    if (c.kind !== "update" || (c.target._tag !== "goal" && c.target._tag !== "item")) continue
    const k = `${c.target._tag}:${c.target.id}`
    const cur = newest.get(k)
    if (!cur || isNewer(c, cur)) newest.set(k, c)
  }
  return newest
}

// ---- milestones -----------------------------------------------------------------------------------------------

/** One row of the milestone strip: an open release, or an active goal a person judges (decision 0035). */
export type MilestoneRow =
  | {
      readonly _tag: "release"
      readonly item: Item
      readonly phase: Phase
      readonly dueAt: number | null
      readonly progress: Progress
      /** Counted leaves created in the last week. */
      readonly added: number
      readonly breakdown: Breakdown
      /** Open `question` descendants (they keep the release open; not counted in progress). */
      readonly questions: ReadonlyArray<Item>
      /** Open `idea` descendants (they keep the release open; not counted in progress). */
      readonly ideas: ReadonlyArray<Item>
      readonly update: Comment | null
      /** The `Left:` line of its newest update, in planning only (Lea's requirements without items). */
      readonly left: string | null
    }
  | {
      readonly _tag: "goal"
      readonly goal: Goal
      readonly phase: "building"
      readonly dueAt: number | null
      readonly update: Comment | null
    }

/**
 * Open releases (not done, not cancelled) and active judged goals (`target: null`), by due date (none last), then
 * releases before goals, then rank.
 */
export const milestonesOf = (
  s: ProjectState,
  now: number,
  updates: ReadonlyArray<Comment>,
  ix?: TreeIndex,
): ReadonlyArray<MilestoneRow> => {
  const x = ix ?? treeIndexOf(s)
  const newest = newestUpdates(updates)
  const since = now - WEEK_MS
  const releases = s.items
    .filter((t) => isRelease(t) && t.status !== "done" && t.status !== "cancelled")
    .sort((a, b) => byDue(a.dueAt, b.dueAt) || byRank(a, b))
  const goals = s.goals
    .filter((g) => g.status === "active" && g.target === null)
    .sort((a, b) => byDue(a.deadline, b.deadline) || byRank(a, b))
  const releaseRows: MilestoneRow[] = releases.map((t) => {
    const leaves = progressLeaves(s, t.id, x)
    const questions: Item[] = []
    const ideas: Item[] = []
    for (const d of indexedDescendants(x, t.id)) {
      if (!isOpenItem(d)) continue
      if (d.type === "question") questions.push(d)
      else if (d.type === "idea") ideas.push(d)
    }
    const phase = phaseOf(t.status)
    const update = newest.get(`item:${t.id}`) ?? null
    return {
      _tag: "release",
      item: t,
      phase,
      dueAt: t.dueAt,
      progress: progressFromLeaves(leaves),
      added: countSince(leaves, since),
      breakdown: breakdownFromLeaves(leaves),
      questions,
      ideas,
      update,
      left: phase === "planning" && update ? updateLine(update.body, "Left") : null,
    }
  })
  const goalRows: MilestoneRow[] = goals.map((g) => ({
    _tag: "goal",
    goal: g,
    phase: "building",
    dueAt: g.deadline,
    update: newest.get(`goal:${g.id}`) ?? null,
  }))
  // merge by due date (both lists are sorted by due, then rank); on equal due a release comes first
  const out: MilestoneRow[] = []
  let i = 0
  let j = 0
  while (i < releaseRows.length || j < goalRows.length) {
    const r = releaseRows[i]
    const g = goalRows[j]
    if (r && (!g || byDue(r.dueAt, g.dueAt) <= 0)) {
      out.push(r)
      i++
    } else if (g) {
      out.push(g)
      j++
    }
  }
  return out
}

// ---- goals ----------------------------------------------------------------------------------------------------

/** A KPI goal with its latest sample (judged goals are milestones). */
export interface GoalRow {
  readonly goal: Goal
  readonly kpi: Kpi | null
  /** "41%"; null = no number yet. */
  readonly value: string | null
  /** "the week of 2026-10-05"; null = no number yet. */
  readonly period: string | null
  /** ">= 50" */
  readonly target: string | null
  readonly update: Comment | null
}

/** Active goals with a target, by rank: the target's KPI, its latest sample, the newest update on the goal. */
export const goalRowsOf = (s: ProjectState, updates: ReadonlyArray<Comment>): ReadonlyArray<GoalRow> => {
  const newest = newestUpdates(updates)
  return s.goals
    .filter((g) => g.status === "active" && g.target !== null)
    .sort(byRank)
    .map((g) => {
      const kpi = g.target ? (kpiOf(s, g.target.kpiId) ?? null) : null
      const latest = kpi?.latest ?? null
      return {
        goal: g,
        kpi,
        value: kpi && latest ? kpiValueText(kpi, latest.value) : null,
        period: kpi && latest ? kpiPeriodLabel(kpi.cadence, latest.at) : null,
        target: g.target ? `${g.target.op} ${g.target.value}` : null,
        update: newest.get(`goal:${g.id}`) ?? null,
      }
    })
}

// ---- next up --------------------------------------------------------------------------------------------------

export interface NextUpRow {
  readonly item: Item
  /** running = a live attempt; ready = a worker leaf the scheduler starts next; person = a person's turn. */
  readonly why: "running" | "ready" | "person"
  readonly runId: RunId | null
  readonly userId: UserId | null
  /** The release it is under (its top-level item, when that is a release). */
  readonly release: Item | null
}

/**
 * Decision 0070 (TRI-22): a leaf that will start on its own but not now: in its retry backoff (`retrying`, until `at`)
 * or with an attempt waiting for a runner (`waiting_for_runner`, since `at`). The web words it ("retrying at 14:05",
 * "waiting for a runner").
 */
export interface NextUpWaitingRow {
  readonly item: Item
  readonly why: "retrying" | "waiting_for_runner"
  readonly at: number
  readonly runId: RunId | null
  readonly release: Item | null
}

export const NEXT_UP_MAX = 8

/**
 * Leaves (not containers, not plannable, not idea or question) in three groups: running (by the run's `requestedAt`,
 * newest first), ready (rank), person (readiness `with_person`, or an open person-assigned `in_progress` leaf whatever
 * its readiness, e.g. under a Next release; by due date, then rank). Each item once; capped at NEXT_UP_MAX. A running
 * attempt that waits for a runner, and a leaf in its retry backoff, are in `waiting` (TRI-22; by `at`), which shares the
 * cap and comes after ready and before a person's turn.
 * `exclude`: items shown elsewhere (the web's Needs-you rows), left out before the cap so they don't take its slots and
 * `more` counts only what is hidden.
 */
export const nextUpOf = (
  s: ProjectState,
  now: number,
  ix?: TreeIndex,
  exclude?: ReadonlySet<string>,
): {
  readonly rows: ReadonlyArray<NextUpRow>
  readonly waiting: ReadonlyArray<NextUpWaitingRow>
  readonly more: number
} => {
  const x = ix ?? treeIndexOf(s)
  const byId = itemsById(s)
  const waiting: NextUpWaitingRow[] = []
  const running: Array<{ row: NextUpRow; at: number }> = []
  const ready: NextUpRow[] = []
  const person: NextUpRow[] = []
  for (const t of s.items) {
    if (t.status !== "todo" && t.status !== "in_progress") continue
    if (exclude?.has(t.id)) continue
    if (x.containers.has(t.id) || isPlannable(t) || NOT_COUNTED_TYPES.has(t.type)) continue
    const personWork = t.status === "in_progress" && t.assignee?._tag === "user"
    const top = topOf(byId, t)
    // cheap pre-checks: readiness can only be running / ready / with_person for a live run, or a worker or person leaf
    // that is top-level or under a Now top-level item (rule 8)
    if (t.activeRunId === null && !personWork) {
      if (t.recurrence !== null) continue
      if (t.assignee?._tag !== "worker" && t.assignee?._tag !== "user") continue
      if (top.id !== t.id && top.status !== "in_progress") continue
    }
    const release = top.id !== t.id && isRelease(top) ? top : null
    const r = readiness(s, t.id, now)
    const run = r._tag === "running" ? runOf(s, r.runId) : undefined
    if (r._tag === "running" && run?.state._tag === "waiting")
      waiting.push({ item: t, why: "waiting_for_runner", at: run.state.since, runId: r.runId, release })
    else if (r._tag === "retry_backoff")
      waiting.push({ item: t, why: "retrying", at: r.until, runId: null, release })
    else if (r._tag === "running")
      running.push({
        row: { item: t, why: "running", runId: r.runId, userId: null, release },
        at: runOf(s, r.runId)?.requestedAt ?? 0,
      })
    else if (r._tag === "ready") ready.push({ item: t, why: "ready", runId: null, userId: null, release })
    else if (r._tag === "with_person")
      person.push({ item: t, why: "person", runId: null, userId: r.userId, release })
    else if (personWork && t.assignee?._tag === "user")
      person.push({ item: t, why: "person", runId: null, userId: t.assignee.userId, release })
  }
  running.sort((a, b) => b.at - a.at || byRank(a.row.item, b.row.item))
  ready.sort((a, b) => byRank(a.item, b.item))
  person.sort((a, b) => byDue(a.item.dueAt, b.item.dueAt) || byRank(a.item, b.item))
  waiting.sort((a, b) => a.at - b.at || byRank(a.item, b.item))
  // one cap for both: running, ready, waiting, then a person's turn
  const head = [...running.map((r) => r.row), ...ready]
  const shown = Math.min(NEXT_UP_MAX, head.length + waiting.length + person.length)
  const waitingShown = waiting.slice(0, Math.max(0, NEXT_UP_MAX - head.length))
  const rows = [...head, ...person].slice(0, shown - waitingShown.length)
  return {
    rows,
    waiting: waitingShown,
    more: Math.max(0, head.length + waiting.length + person.length - NEXT_UP_MAX),
  }
}

// ---- to look at -----------------------------------------------------------------------------------------------

/** Output kinds the Overview shows as thumbnails. */
export const LOOK_AT_KINDS: ReadonlySet<ArtifactKind> = new Set<ArtifactKind>(["image", "report", "document"])
export const LOOK_AT_WINDOW_MS = 14 * 86_400_000
export const LOOK_AT_MAX = 8

export interface LookAtRow {
  readonly artifact: Artifact
  readonly item: Item | null
  /** An open human review card of its item names it. */
  readonly waiting: boolean
}

/**
 * Image, report and document outputs (files) of the last 14 days that are the newest of their version chain (no
 * non-rejected artifact supersedes them; a rejected head stays, shown with ✕). Waiting for review first, then newest.
 */
export const toLookAtOf = (
  s: ProjectState,
  now: number,
): { readonly rows: ReadonlyArray<LookAtRow>; readonly more: number } => {
  const since = now - LOOK_AT_WINDOW_MS
  const superseded = new Set<string>()
  for (const a of s.artifacts)
    if (a.supersedes !== null && a.state !== "rejected") superseded.add(a.supersedes)
  const heads = s.artifacts.filter(
    (a) => LOOK_AT_KINDS.has(a.kind) && isOutput(a) && a.createdAt >= since && !superseded.has(a.id),
  )
  const named = new Set<string>()
  const itemIds = new Set<ItemId>()
  for (const a of heads) if (a.itemId !== null) itemIds.add(a.itemId)
  for (const itemId of itemIds)
    for (const p of openReviewCards(s, itemId)) for (const o of reviewOutputs(s, p)) named.add(o.artifact.id)
  const byId = itemsById(s)
  const rows: LookAtRow[] = heads.map((a) => ({
    artifact: a,
    item: a.itemId !== null ? (byId.get(a.itemId) ?? null) : null,
    waiting: named.has(a.id),
  }))
  rows.sort((a, b) => Number(b.waiting) - Number(a.waiting) || b.artifact.createdAt - a.artifact.createdAt)
  return { rows: rows.slice(0, LOOK_AT_MAX), more: Math.max(0, rows.length - LOOK_AT_MAX) }
}

// ---- decisions ------------------------------------------------------------------------------------------------

/** One entry of "what did we decide": a decided card or a decision comment. */
export type DecisionEntry =
  | {
      readonly _tag: "card"
      readonly proposal: Proposal
      readonly at: number
      readonly answer: string | null
      readonly subject: EntityRef | null
    }
  | { readonly _tag: "comment"; readonly comment: Comment; readonly at: number; readonly subject: EntityRef }

export const DECISIONS_MAX = 20

/** Card kinds that are not project decisions: one card per CLI permission prompt, and memory writes. */
export const NOT_DECISION_BODIES: ReadonlySet<ProposalBody["_tag"]> = new Set<ProposalBody["_tag"]>([
  "permission",
  "memory",
  "monitor", // wave C (decision 0071): a monitor card is a setting, like a memory card
])

/**
 * A resolved card's answer in one line (question: the option and free text; plan_change: accepted count; else the
 * note). `keyed` (agents, read_graph) names the option as `key: label`; `label` (people, the search index) only its
 * label.
 */
export const decisionAnswer = (p: Proposal, mode: "keyed" | "label" = "keyed"): string | null => {
  const r = p.resolution
  if (!r) return null
  if (p.body._tag === "question") {
    const option = r.optionKey ? p.body.options.find((o) => o.key === r.optionKey) : undefined
    const parts = [
      option ? (mode === "label" ? option.label : `${option.key}: ${option.label}`) : r.optionKey,
      r.text ? `"${r.text}"` : null,
      r.note ? `note: "${r.note}"` : null,
    ].filter((x): x is string => !!x)
    return parts.length > 0 ? parts.join(" · ") : null
  }
  if (p.body._tag === "plan_change") {
    // decision 0070 (TRI-19): a rejected plan reads "rejected" (with the reason), never "0 of 1 changes accepted";
    // an expired plan was decided by no one; an adopted one was re-filed by the lead. Counts only for decided cards
    const accepted = p.perChange.filter((c) => c === "accepted").length
    if (p.state === "rejected") return r.note ? `rejected: ${r.note}` : "rejected"
    if (p.state === "expired") return "expired, nothing applied"
    if (p.state === "adopted") return r.note ?? "taken over by the lead"
    if (p.perChange.length > 0) return `${accepted} of ${p.perChange.length} changes accepted`
  }
  return r.note
}

/**
 * The record a card is about: a question's subject; an outward action's item; a plan's first change naming an item
 * (`itemId`) or a goal (`goalId`), else null (a brief-only plan); permission and memory cards: null.
 */
export const decisionSubject = (p: Proposal): EntityRef | null => {
  const b = p.body
  switch (b._tag) {
    case "question":
      return b.subject
    case "outward_action":
      return { _tag: "item", id: b.itemId }
    case "plan_change":
      for (const c of b.changes) {
        const rec = c as unknown as Record<string, unknown>
        if (typeof rec.itemId === "string") return { _tag: "item", id: rec.itemId as ItemId }
        if (typeof rec.goalId === "string") return { _tag: "goal", id: rec.goalId as Goal["id"] }
      }
      return null
    case "permission":
    case "memory":
    case "monitor": // wave C (decision 0071)
    case "project_request":
      return null
  }
}

/**
 * Decided cards (not open, resolved, not withdrawn, not permission or memory; `expired` stays) and decision comments
 * on items and goals, newest first (card: `resolution.at`, comment: `createdAt`). Equal times: cards first, cards in
 * `s.proposals` order, comments in input order. At most `limit`.
 *
 * `decidedCards` (wave K, K0): cards from the Project DO's `decided` projection, so a card compacted out of the state
 * after 7 days still counts. A card in both is taken from the state (by id); projection cards follow the state's in
 * input order.
 */
export const recentDecisions = (
  s: ProjectState,
  decisionComments: ReadonlyArray<Comment>,
  limit: number = DECISIONS_MAX,
  decidedCards: ReadonlyArray<Proposal> = [],
  /** `decisionAnswer`'s mode: `keyed` for agents (read_graph), `label` for people (the Overview; TRI-19). */
  mode: "keyed" | "label" = "keyed",
): ReadonlyArray<DecisionEntry> => {
  const inState = new Set(s.proposals.map((p) => p.id))
  const cards: DecisionEntry[] = [...s.proposals, ...decidedCards.filter((p) => !inState.has(p.id))]
    .filter(
      (p) =>
        !isOpenProposal(p) &&
        p.resolution !== null &&
        p.state !== "withdrawn" &&
        !NOT_DECISION_BODIES.has(p.body._tag),
    )
    .map((p) => ({
      _tag: "card",
      proposal: p,
      at: p.resolution?.at ?? p.updatedAt,
      answer: decisionAnswer(p, mode),
      subject: decisionSubject(p),
    }))
  const comments: DecisionEntry[] = []
  for (const c of decisionComments)
    if (c.kind === "decision" && (c.target._tag === "item" || c.target._tag === "goal"))
      comments.push({ _tag: "comment", comment: c, at: c.createdAt, subject: c.target })
  // Array.prototype.sort is stable: equal times keep cards before comments, each in input order
  return [...cards, ...comments].sort((a, b) => b.at - a.at).slice(0, Math.max(0, limit))
}

// ---- open questions, implemented, later ----------------------------------------------------------------------

/** The nearest release above `t` (an item of type `release`), else null. */
const releaseAbove = (byId: ReadonlyMap<string, Item>, t: Item): Item | null => {
  const seen = new Set<string>([t.id])
  let cur = t.parentId === null ? undefined : byId.get(t.parentId)
  while (cur && !seen.has(cur.id)) {
    if (cur.type === "release") return cur
    seen.add(cur.id)
    cur = cur.parentId === null ? undefined : byId.get(cur.parentId)
  }
  return null
}

/** Open `question` items (triage included) with their nearest release; by the release's due date (none last), then rank. */
export const openQuestionsOf = (
  s: ProjectState,
): ReadonlyArray<{ readonly item: Item; readonly release: Item | null }> => {
  const byId = itemsById(s)
  return s.items
    .filter((t) => t.type === "question" && isOpenItem(t))
    .map((item) => ({ item, release: releaseAbove(byId, item) }))
    .sort((a, b) => byDue(a.release?.dueAt ?? null, b.release?.dueAt ?? null) || byRank(a.item, b.item))
}

const refNumber = (ref: string): number => {
  const m = /(\d+)$/.exec(ref)
  return m ? Number(m[1]) : 0
}

/**
 * Done releases by `closedAt`, newest first (ties: the ref's number, highest first); each with its features: its direct
 * children with status done that are not idea or question, by rank.
 */
export const implementedOf = (
  s: ProjectState,
  ix?: TreeIndex,
): ReadonlyArray<{ readonly release: Item; readonly features: ReadonlyArray<Item> }> => {
  const x = ix ?? treeIndexOf(s)
  return s.items
    .filter((t) => isRelease(t) && t.status === "done")
    .sort((a, b) => (b.closedAt ?? 0) - (a.closedAt ?? 0) || refNumber(b.ref) - refNumber(a.ref))
    .map((release) => ({
      release,
      features: (x.children.get(release.id) ?? [])
        .filter((c) => c.status === "done" && !NOT_COUNTED_TYPES.has(c.type))
        .sort(byRank),
    }))
}

/** Ideas parked for later (type idea, status backlog, any depth) and the Triage count. */
export const laterOf = (s: ProjectState): { readonly ideas: number; readonly triage: number } => {
  let ideas = 0
  let triage = 0
  for (const t of s.items) {
    if (t.type === "idea" && t.status === "backlog") ideas++
    if (t.status === "triage") triage++
  }
  return { ideas, triage }
}

/** The lead's newest `update` (by `createdAt`, then `contentSeq`); a person's update never counts. */
export const leadLatestOf = (s: ProjectState, updates: ReadonlyArray<Comment>): Comment | null => {
  const lead = s.project?.leadAgentId ?? null
  if (lead === null) return null
  let best: Comment | null = null
  for (const c of updates) {
    if (c.kind !== "update" || c.createdBy._tag !== "agent" || c.createdBy.agentId !== lead) continue
    if (best === null || isNewer(c, best)) best = c
  }
  return best
}

// ---- the whole Overview ---------------------------------------------------------------------------------------

export interface Overview {
  readonly milestones: ReadonlyArray<MilestoneRow>
  readonly goals: ReadonlyArray<GoalRow>
  readonly nextUp: {
    readonly rows: ReadonlyArray<NextUpRow>
    readonly waiting: ReadonlyArray<NextUpWaitingRow>
    readonly more: number
  }
  readonly toLookAt: { readonly rows: ReadonlyArray<LookAtRow>; readonly more: number }
  readonly leadLatest: Comment | null
  /** The `Next:` line of `leadLatest`. */
  readonly next: string | null
  readonly decisions: ReadonlyArray<DecisionEntry>
  readonly openQuestions: ReadonlyArray<{ readonly item: Item; readonly release: Item | null }>
  readonly implemented: ReadonlyArray<{ readonly release: Item; readonly features: ReadonlyArray<Item> }>
  readonly later: { readonly ideas: number; readonly triage: number }
  /** No items, no goals and an empty brief: the first-run hint. */
  readonly fresh: boolean
}

/**
 * Every Overview block from the state and the three comment lists (`updates` = the newest updates ∪ the newest per
 * target; `decisionComments` = the newest decision comments). One `TreeIndex` for all tree walks.
 */
export const overviewOf = (
  s: ProjectState,
  input: {
    readonly now: number
    readonly updates: ReadonlyArray<Comment>
    readonly decisionComments: ReadonlyArray<Comment>
    /** Items Next up leaves out (the web: what Needs you shows), before its cap. */
    readonly nextUpExclude?: ReadonlySet<string>
    /** Wave K (K0): decided cards from the `decided` projection (`DecidedCards`), for cards compacted out of the state. */
    readonly decidedCards?: ReadonlyArray<Proposal>
  },
): Overview => {
  const ix = treeIndexOf(s)
  const leadLatest = leadLatestOf(s, input.updates)
  return {
    milestones: milestonesOf(s, input.now, input.updates, ix),
    goals: goalRowsOf(s, input.updates),
    nextUp: nextUpOf(s, input.now, ix, input.nextUpExclude),
    toLookAt: toLookAtOf(s, input.now),
    leadLatest,
    next: leadLatest ? updateLine(leadLatest.body, "Next") : null,
    decisions: recentDecisions(s, input.decisionComments, DECISIONS_MAX, input.decidedCards ?? [], "label"),
    openQuestions: openQuestionsOf(s),
    implemented: implementedOf(s, ix),
    later: laterOf(s),
    fresh: s.items.length === 0 && s.goals.length === 0 && (s.project?.description ?? "").trim() === "",
  }
}
